import { animate, style, transition, trigger } from '@angular/animations';
import { AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AppService } from 'app/app.service';
import { AssetFilterService, AssetListItem, AssetStateService } from 'app/services';
import { BaseComponent, MapButton, MapComponent, MapControls, MapZonesService } from 'app/shared/components';
import { MAP_COLORS, MAP_COLORS_ENUM, getIconSvgString } from 'app/shared/components/map/map.markers';
import * as moment from 'moment-timezone';
import { Observable, ReplaySubject, combineLatest, of } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { OverviewStateService } from '../../services/state.service';

@Component({
    selector: 'key-overview-map',
    styleUrls: ['./map.component.scss'],
    templateUrl: './map.component.html',
    animations: [
        trigger('previewToggleAnimation', [
            transition(':enter', [
                style({ maxHeight: 0 }),
                animate('280ms', style({ maxHeight: '500px' })),
            ]),
            transition(':leave', [
                style({ maxHeight: '500px' }),
                animate('280ms', style({ maxHeight: 0 })),
            ]),
        ]),
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        MapZonesService
    ]
})
export class OverviewMapComponent extends BaseComponent implements AfterViewInit, OnDestroy {

    private readonly DEFAULT_ZOOM = 15;

    @ViewChild('map', { static: true }) map: MapComponent;

    private prevCoords: { x: number, y: number };
    private hiddenAssets: string[] = [];
    private _displayNameField: string;
    private displayNameField$ = new ReplaySubject<string>(1);
    selectedAsset: AssetListItem;
    empty = true;

    mapControls = [
        MapControls.zoom,
        MapControls.fullscreen,
        MapControls.recenter,
        MapControls.zoneedit,
        MapControls.zonedelete,
        MapControls.zoneadd,
        MapControls.googlemaps,
        MapControls.layers,
        MapControls.measurements,
        MapControls.search,
        MapControls.coordinates,
        MapControls.routing,
        MapControls.findclosest,
        this.app?.features?.page?.mapsearch?.enabled && MapControls.mapsearch,
    ].filter(x => x);

    @Input() set displayNameField(field: string) {
        this._displayNameField = field;
        this.displayNameField$.next(field);
    }

    @Input() buttons: MapButton[];
    @Input() isMobile: boolean;
    @Input() set assets(assets: AssetListItem[]) {
        setTimeout(() => {
            this.invalidateAssetsState(assets);
            this.map.invalidate();
            if (this.selectedAsset) {
                this.selectedAsset = assets.find(x => x.id === this.selectedAsset.id) || this.selectedAsset;
                if (this.empty && !this.filters.point) {
                    this.selectMarker(this.selectedAsset);
                }
                this.updateGeoLock(this.selectedAsset);
                this.followAsset(this.selectedAsset);
            } else {
                if (this.empty && !this.filters.point) {
                    try {
                        this.map.zoomToBounds(this.map.getMarkerBounds());
                    } catch {
                        // for some unknown reason this fails on tests sometimes
                    }
                }
            }
            this.empty = !assets;
        });
    }
    @Output() onButtonClicked = new EventEmitter<string>();

    constructor(
        public router: Router,
        public zonesService: MapZonesService,
        public assetState: AssetStateService,
        public filters: AssetFilterService,
        public overviewState: OverviewStateService,
        private app: AppService
    ) {
        super();
    }


    ngAfterViewInit() {
        this.zonesService.attachMap(this.map);

        this.overviewState.selectedAsset$
            .pipe(takeUntil(this.destroyed))
            .subscribe(asset => {
                if ((asset && this.selectedAsset && this.selectedAsset.id !== asset.id) || (asset && !this.selectedAsset)) {
                    this.prevCoords = null;
                    setTimeout(() => {
                        this.selectMarker(asset);
                        this.updateGeoLock(asset);
                    }, 50);
                }
                this.selectedAsset = asset;

            });

        this.overviewState.assetsShown$
            .pipe(takeUntil(this.destroyed))
            .subscribe(assetIds => {
                const assetsState = assetIds.map(assetId => this.assetState.assetListIndex[assetId]);
                this.invalidateAssetsState(assetsState);
            });

        this.overviewState.assetsHidden$
            .pipe(takeUntil(this.destroyed))
            .subscribe(assetIds => {
                assetIds.forEach(assetId => {
                    this.map.removeMapMarker({
                        id: assetId,
                    });
                });
            });

        this.overviewState.hiddenMapAssets$
            .pipe(takeUntil(this.destroyed))
            .subscribe(mask => {
                this.hiddenAssets = mask;
            });

        this.displayNameField$
            .pipe(takeUntil(this.destroyed))
            .subscribe(() => {
                // the asset display name has been changed, we have to update the map icons.
                this.invalidateAssetsState(this.assetState.assets);
            });

        this.on(this.overviewState.zoomTo$, value => {
            this.map.zoomTo(value.lat, value.lon, value.zoom);
        });

        this.invalidateAssetsState(this.assetState.assets);
    }

    ngOnDestroy() {
        if (this) { // there is either a bug with the destroy or HMR does weird things, but this is undefined in hot module reloads
            this.zonesService.detachMap();
            this.triggerDestroyed();
            this.unsubscribeAll();
        }
    }

    reset() {
        this.map.clearMarkers();
        this.map.invalidate();
        this.empty = true;
    }

    resize() {
        this.map.invalidate();
    }

    selectMarker(asset: AssetListItem) {
        this.map.selectMarker(asset ? asset.id : null, true, this.getZoom());
    }

    updateGeoLock(asset: AssetListItem) {
        if (asset && asset.geoLock && moment().isBetween(asset.geoLock.start, asset.geoLock.end)) {
            this.map.addZone({
                id: 'geolock',
                name: asset.name,
                center: { x: asset.geoLock.lon, y: asset.geoLock.lat },
                color: '#ff6e00',
                radius: asset.geoLock.radiusKm,
                type: 'zone',
                modifiedDate: asset.geoLock.start,
                interactive: false
            });
        } else {
            if (asset?.telemetry?.telemetry?.gl_lon) {
                this.map.addZone({
                    id: 'geolock',
                    name: asset.name,
                    center: {
                        x: asset.telemetry.telemetry.gl_lon,
                        y: asset.telemetry.telemetry.gl_lat,
                    },
                    color: '#ff6e00',
                    radius: asset.telemetry.telemetry.gl_rad,
                    type: 'zone',
                    modifiedDate: asset.telemetry.date,
                    interactive: false
                });
            } else {
                this.map.removeZone('geolock');
            }
        }
    }

    selectAsset(asset: AssetListItem) {
        this.overviewState.navigateToAsset(asset && asset.id);
    }

    getIconColor(asset: AssetListItem): string {
        return ((typeof asset.color === 'number')
            ? MAP_COLORS[asset.color || 0]
            : MAP_COLORS[MAP_COLORS_ENUM[asset.color] || 0]);
    }

    getAssetIconHtml(asset: AssetListItem, focused: boolean = false): { size: number, html: string } {

        const iconSize = focused ? 35 : 30;

        let iconType = 'idle';
        if (!!asset.telemetry && asset.telemetry.active) {
            if (!!asset.telemetry.location && asset.telemetry.location.speed === 0) {
                iconType = 'stop';
            } else {
                iconType = 'direction';
            }
        }

        return {
            size: iconSize,
            html: `
            <div class="asset-type">
              ${getIconSvgString(this.app.theme, iconType, {
                fill: this.getIconColor(asset),
                rotation: asset['telemetry'].location.heading,
                size: iconSize,
            })}
            </div>`,
        };

    }

    /** Updates assets on the map */
    invalidateAssetsState(assets: AssetListItem[]) {
        if (assets) {
            assets
                .filter(asset => asset?.telemetry?.location)
                .filter(asset => !this.hiddenAssets.includes(asset.id))
                .map(asset => {

                    let radius = 0;
                    if (asset.telemetry?.telemetry?.gps_acc !== undefined) {
                        radius = asset.telemetry?.telemetry?.gps_acc;
                        if (radius < 0) { radius = 1000; }
                    }

                    this.map.setMarker({
                        id: asset.id,
                        name: this.getDisplayName(asset, this._displayNameField),
                        color: this.getIconColor(asset),
                        flags: asset.telemetry.state ? Object.values(asset.telemetry.state).map(x => ({ icon: 'tag', color: x.color })) : null,
                        lat: asset.telemetry.location.lat,
                        lon: asset.telemetry.location.lon,
                        radius: radius / 1000, // convert to km
                        click: () => {
                            this.selectAsset(asset);
                        },
                        getIcon: (selected: boolean) => {
                            return this.getAssetIconHtml(asset, selected);
                        },
                    });
                });
        } else {
            this.reset();
        }
    }

    /** Retrieve linked assets for display on the info window */
    getLinkedAssets(asset: AssetListItem): Observable<any> {
        // TODO: remove, this information should be available from the poll/overview service once created
        const linkedAssets = asset.telemetry.linked.filter(linked => linked.id !== asset.id);
        if (!linkedAssets.length) {
            return of([]);
        }
        return combineLatest(...linkedAssets.map(linked => this.app.api.entities.getAsset(linked.id)));
    }

    /**
     * Implements an algorithm to decide whether to pan to an assets location. If the asset is selected
     * and visible on the screen, panning will only occur when it leaves the screen. If the user pans
     * away from the asset, auto panning will stop, until such time as the asset is in view again (and leaves).
     */
    followAsset(asset: AssetListItem) {

        if (asset && asset.telemetry && asset.telemetry.location) {
            const coords = { x: asset.telemetry.location.lon, y: asset.telemetry.location.lat };
            if (this.prevCoords) {
                const zoom = this.map.getZoom();
                const bounds = this.map.getBounds();
                const inBounds = (c: { x: number, y: number }): boolean => {
                    return (c.x >= bounds.left && c.x <= bounds.right && c.y <= bounds.top && c.y >= bounds.bottom);
                };
                if (inBounds(this.prevCoords) && !inBounds(coords)) {
                    // the asset was in view, but isn't anymore, we need to zoom
                    this.map.zoomTo(coords.y, coords.x, zoom);
                }
            }
            this.prevCoords = coords;
        } else {
            this.prevCoords = null;
        }
    }

    zoomToAsset(asset: AssetListItem) {
        if (asset && asset.telemetry && asset.telemetry.location) {
            const coords = { x: asset.telemetry.location.lon, y: asset.telemetry.location.lat };
            this.map.zoomTo(coords.y, coords.x, this.getZoom());
        }
    }

    deselectAssets() {
        this.overviewState.navigateToRoot();
    }

    buttonClicked(id: string) {
        this.onButtonClicked.emit(id);
    }

    private getZoom(): number {
        return Math.max(this.map.getZoom(), this.DEFAULT_ZOOM);
    }

    private getDisplayName(asset: AssetListItem, field: string): string {
        const displayName = asset.fields[field];
        if (displayName) {
            return displayName;
        }
        return asset.name;
    }
}
